import { trackSignupModalOpenBrandpageBottomCtaClick } from "@faire/web-api/events/signup/click/modalOpenBrandpageBottomCta";
import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import { IGetBrandTopCategoryResponse } from "@faire/web-api/indigofair/data/IGetBrandTopCategoryResponse";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web/common/trackEvent";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { Suspense, useMemo } from "react";
import styled from "styled-components";

import { Footer } from "@faire/retailer/components/Footer";
import { LazyLoggedOutMobileSignUpSnackbar } from "@faire/retailer/components/LoggedOutMobileSignUpSnackbar";
import { SignUpBanner } from "@faire/retailer/components/SignUpBanner/SignUpBanner";
import { buildBrandReviewSummaryFromBrandReviews } from "@faire/retailer/lib/buildReviewSummary";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import {
  BrandOrderReviewPageNavigationSourceContext,
  BrandOrderReviewPageNavigationSource,
} from "@faire/retailer/lib/useBrandOrderReviewPageNavigationSourceContext";
import { BrandReviews } from "@faire/retailer/views/brand/BrandReviews";
import { BottomSupplierRecommendationCarousel } from "@faire/retailer/views/brand/logged-out/components/BottomSupplierRecommendationCarousel";
import { CategoriesCarousel } from "@faire/retailer/views/brand/logged-out/components/CategoriesCarousel";
import { RelatedSearches } from "@faire/retailer/views/brand/logged-out/components/RelatedSearches";
import { SimilarBrandsCarousel } from "@faire/retailer/views/brand/logged-out/components/SimilarBrandsCarousel";
import { BrandPageLowerPart as BrandReviewsContainer } from "@faire/retailer/views/brand/styled";

import {
  BRAND_REVIEWS_SECTION_FRAGMENT_IDENTIFIER,
  SCROLL_PATH,
} from "../shared/consts";

type Props = {
  brandToken: string;
  brand: IBrandViewV2Response.ILeanBrand;
  topCategory: IGetBrandTopCategoryResponse | undefined;
};

const BelowTheFoldBase: React.FC<Props> = ({
  brandToken,
  brand,
  topCategory,
}) => {
  const isSSR = useIsSSR();
  const getBrandReviewSummary = useMemo(
    () => buildBrandReviewSummaryFromBrandReviews(brand?.brand_reviews_summary),
    [brand?.brand_reviews_summary]
  );

  const similarBrandsRef = useDoActionWhenVisible(() => {
    trackEvent(`${SCROLL_PATH}/similar-brands`);
  });
  const categoriesRef = useDoActionWhenVisible(() => {
    trackEvent(`${SCROLL_PATH}/categories`);
  });
  const signUpBannerRef = useDoActionWhenVisible(() => {
    trackEvent(`${SCROLL_PATH}/cta-banner`);
  });
  const footerRef = useDoActionWhenVisible(() => {
    trackEvent(`${SCROLL_PATH}/footer`);
  });

  return (
    <>
      <StyledSimilarBrandsCarousel
        brandToken={brandToken}
        topCategory={topCategory}
        brandName={brand.name}
        ref={similarBrandsRef}
      />
      {getBrandReviewSummary ? (
        <>
          <Spacer height="1x" />
          <Anchor id={BRAND_REVIEWS_SECTION_FRAGMENT_IDENTIFIER} />
          <BrandReviewsContainer>
            <BrandOrderReviewPageNavigationSourceContext.Provider
              value={BrandOrderReviewPageNavigationSource.logged_out_brand_page}
            >
              <BrandReviews
                brandReviewSummary={getBrandReviewSummary}
                brand={brand}
              />
            </BrandOrderReviewPageNavigationSourceContext.Provider>
          </BrandReviewsContainer>
          <Spacer height="5x" />
        </>
      ) : null}
      <WhenVisibilityIsImminent>
        <BottomSupplierRecommendationCarousel brandToken={brandToken} />
      </WhenVisibilityIsImminent>
      <WhenVisibilityIsImminent>
        <RelatedSearches brandToken={brandToken} />
      </WhenVisibilityIsImminent>
      <CategoriesCarousel
        topCategoryPath={topCategory?.category_path_identifier}
        ref={categoriesRef}
      />
      <Spacer height="9x" />
      <SignUpBanner
        onClick={() =>
          popSignUp(
            SignUpType.BRAND,
            trackSignupModalOpenBrandpageBottomCtaClick,
            {
              element: SignUpElement.BELOW_FOLD,
              sourceId: "brandpage_below_the_fold_cta_banner",
            },
            { brand: brandToken }
          )
        }
        ref={signUpBannerRef}
      />
      <Footer ref={footerRef} />
      {!isSSR ? (
        <Suspense fallback={null}>
          <LazyLoggedOutMobileSignUpSnackbar />
        </Suspense>
      ) : null}
    </>
  );
};

export const BelowTheFold = React.memo(BelowTheFoldBase);

const StyledSimilarBrandsCarousel = styled(SimilarBrandsCarousel)`
  margin-bottom: ${getSpacing("5x")};
`;
